import React, { useEffect, useState } from 'react'

import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'

import { useAxiosPrivate, useFetch } from '../../../hooks'
import { useTheme } from '@emotion/react'
import { matchSorter } from 'match-sorter'
import { VALIDATION_RULES } from '../../../constants/constants'

const isEmpty = (value) =>
  typeof value === 'object' ? Object.values(value).every((val) => !val) : !value

const isExist = (dataSourceList, property, value) => {
  return dataSourceList.find((dataSource) => dataSource[property] === value)
}

const canIncludeInitialOption = (dataSourceList, property, value) =>
  !isExist(dataSourceList, property, value)

const OptionItem = (props) => {
  const { optionLabelProp, optionItemProps, searchResult } = props
  const theme = useTheme()
  const optionLabels =
    typeof optionLabelProp === 'object' ? (
      <ListItemText
        primary={searchResult[optionLabelProp?.primary]}
        secondary={searchResult[optionLabelProp?.secondary]}
      />
    ) : (
      <ListItemText primary={searchResult[optionLabelProp]} />
    )

  return (
    <ListItem
      {...optionItemProps}
      sx={{
        // to fix hover issue
        '&:hover': {
          background: theme.palette.action.hover,
        },
        display: isEmpty(searchResult) ? 'none' : 'initial',
      }}
    >
      {optionLabels}
    </ListItem>
  )
}
/**
 * LiveSearch is a reusable component that provides an autocomplete search functionality.
 * It fetches data from a given URL and allows users to search and select options.
 *
 * @component
 * @param {Object} props - The props for the LiveSearch component.
 * @param {string} props.url - The API endpoint to fetch search data. (Required)
 * @param {string} props.name - The name of the input field. (Required)
 * @param {string} [props.targetName] - The name of the secondary input field.
 * @param {string} props.value - The initial value for the primary input field.
 * @param {string} props.targetValue - The initial value for the secondary input field.
 * @param {string|number} props.rowId - The unique identifier for the row.
 * @param {string} [props.idProp='id'] - The property name used as the unique identifier for options.
 * @param {Object|string} props.optionLabelProp - The property or properties used to display option labels.
 * @param {string} props.searchQueryProp - The property used to filter options based on the search query.
 * @param {Function} props.onSelect - Callback function triggered when an option is selected.
 * @param {Function} props.onError - Callback function triggered when validation errors occur.
 * @param {boolean} props.valid - Indicates whether the input is valid.
 * @param {string|boolean} props.error - The error message or validation state.
 * @param {string} props.dataProperty - The property name in the fetched data that contains the options.
 * @param {string} props.noOptionsText - The text displayed when no options are available.
 * @param {boolean} props.readOnly - If true, the input field is read-only.
 * @param {string} props.hint - A hint or helper text displayed below the input field.
 * @param {Object} restProps - Additional props passed to the underlying TextField component.
 *
 * @throws {Error} Throws an error if the `name` or `url` prop is not provided.
 *
 * @returns {JSX.Element} The LiveSearch component.
 */
const LiveSearch = (props) => {
  const {
    url,
    name,
    targetName,
    value,
    targetValue,
    rowId,
    idProp = 'id',
    optionLabelProp,
    searchQueryProp,
    onSelect,
    onError,
    valid,
    error,
    dataProperty,
    noOptionsText,
    readOnly,
    hint,
    ...restProps
  } = props
  if (!name) throw Error('name prop is required')

  if (!url) {
    throw new Error('url prop is required')
  }
  const theme = useTheme()
  const isDarkMode = theme?.palette?.mode === 'dark'
  const [searchResults, setSearchResults] = useState([])
  const [inputVal, setInputValue] = useState(null)
  const [touched, setTouched] = useState(false)
  const axiosPrivate = useAxiosPrivate() 
  // fetching the teams data
  const {loading, data} = useFetch({
    axiosInstance: axiosPrivate,
    path: url,
  })

  const handleError = (inputValue, targetInputValue) => {
    onError(name, VALIDATION_RULES.REQUIRED(inputValue), rowId)
    onError(targetName, VALIDATION_RULES.REQUIRED(targetInputValue), rowId)
  }
  useEffect(() => {
    const compoundValue = {
      [optionLabelProp.primary]: value,
      [optionLabelProp.secondary]: targetValue,
    }
    setInputValue(compoundValue)
    handleError(value, targetValue)

    // eslint-disable-next-line
  }, [value, targetValue])

  useEffect(() => {
    if (data) {
      const dataSource = data[dataProperty]
      setSearchResults(
        canIncludeInitialOption(dataSource, idProp, value)
          ? [inputVal, ...dataSource]
          : dataSource
      )
    }
    // eslint-disable-next-line
  }, [data, dataProperty])

  const handleSelect = (newValue) => {
    let inputValue
    let targetInputValue
    let practice_timezone
    let connected_application
    if (newValue) {
      inputValue = newValue[optionLabelProp?.primary]
      targetInputValue = newValue[optionLabelProp?.secondary]
      practice_timezone = newValue["practice_timezone"]
      connected_application = newValue["connected_application"]
    } else {
      inputValue = ''
      targetInputValue = ''
      practice_timezone = ''
      connected_application = ''
    }
    onSelect(inputValue, targetInputValue, practice_timezone, connected_application, rowId)
    handleError(inputValue, targetInputValue)
  }

  const handleOnBlur = (e) => {
    setTouched(true)
    const match = searchResults.some((result) => {
      const values = Object.values(result)
      return values.some((value) => value && value === e.target.value)
    })
    onError(name, match ? true : 'Please select a value', rowId)
  }
  const finalOptionLabelProp =
    typeof optionLabelProp === 'object'
      ? optionLabelProp?.primary
      : optionLabelProp

  const filterOptions = (options, { inputValue }) => {
    return matchSorter(options, inputValue, {
      keys: inputVal ? Object.keys(inputVal) : [searchQueryProp],
    })
  }

  return (
    <Autocomplete
      clearOnBlur
      onBlur={handleOnBlur}
      readOnly={readOnly}
      loading={loading}
      loadingText="loading"
      value={inputVal}
      onChange={(event, newValue) => {
        setInputValue(newValue)
        handleSelect(newValue)
      }}
      autoHighlight
      id="live-search"
      getOptionLabel={(searchResult) => searchResult[finalOptionLabelProp]}
      filterOptions={filterOptions}
      options={searchResults}
      isOptionEqualToValue={(option, value) => {
        return option[searchQueryProp] === value[searchQueryProp]
      }}
      noOptionsText={noOptionsText}
      renderOption={(props, searchResult) => {
        // when the initial value is empty, we will include the empty value in the results to suppress the warning,
        // but the value will be omitted from the options
        if (isEmpty(searchResult)) return null
        return (
          <OptionItem
            optionLabelProp={optionLabelProp}
            optionItemProps={props}
            searchResult={searchResult}
            key={searchResult[idProp]}
          />
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...restProps}
          name={name}
          error={touched && error && error !== true}
          helperText={touched && error !== true ? error : hint}
          fullWidth
          InputProps={{
            readOnly: readOnly,
            ...params.InputProps,
          }}
          inputProps={{
            ...params.inputProps,
          }}
          sx={{
            '& input': {
              cursor: readOnly ? 'not-allowed' : 'initial',
              backgroundColor: readOnly
                ? `${isDarkMode
                  ? theme.palette.grey[900]
                  : theme.palette.grey[50]
                }`
                : 'initial',
            },
            '& .MuiFormHelperText-root': {
              position: 'absolute',
              bottom: '-1.6em',
              marginLeft: 0,
            },
          }}
        />
      )}
    />
  )
}

export default LiveSearch
